import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import InternalSidebar from '../../components/InternalSidebar';
import InternalTopMenu from '../../components/InternalTopMenu';
import EmailVerificationBanner from '../../components/EmailVerificationBanner';
import posthog from 'posthog-js';
import {
  describeAvailableToday,
  describeLimits,
  describeRefusal,
  getBlockedReason,
  getUpgradePath,
  validateAmount,
} from '../../constants/depositLimits';

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const parseCurrencyInput = (value) => {
  if (!value) return NaN;
  const numeric = Number(String(value).replace(/\./g, '').replace(',', '.').replace(/[^\d.-]/g, ''));
  return Number.isFinite(numeric) ? Number(numeric.toFixed(2)) : NaN;
};

const formatCurrency = (value) => currencyFormatter.format(Number(value) || 0);

const Deposit = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [method, setMethod] = useState('depix'); // depix | bitcoin | ln | liquid
  const [lnDepositEnabled, setLnDepositEnabled] = useState(false);
  const [liquidDepositEnabled, setLiquidDepositEnabled] = useState(false);
  const isEmailVerified = Number(user?.validEmail) === 1;
  const registrationStep = Number(user?.userRegistrationStep ?? user?.registrationStep ?? 0);
  const isLevelOneOrAbove = registrationStep >= 1;

  // Depix state
  const [depixAmountInput, setDepixAmountInput] = useState('');
  const [depixError, setDepixError] = useState('');
  const [depixGlobalError, setDepixGlobalError] = useState('');
  const [depixLoading, setDepixLoading] = useState(false);
  const [depixResult, setDepixResult] = useState(null);
  const [depixLimits, setDepixLimits] = useState(null);
  const [depixLimitsLoading, setDepixLimitsLoading] = useState(false);
  // A falha ao consultar os limites tem estado próprio porque a consulta é refeita depois de uma
  // recusa: se as duas mensagens dividissem o mesmo estado, a recarga apagaria a recusa da tela.
  const [depixLimitsError, setDepixLimitsError] = useState('');
  const [depixRefusalCode, setDepixRefusalCode] = useState('');
  // idle | polling | confirmed | canceled | timeout
  const [depixPaymentStatus, setDepixPaymentStatus] = useState('idle');
  // usado para reiniciar a janela de polling ("continuar aguardando")
  const [depixPollAttempt, setDepixPollAttempt] = useState(0);

  // Bitcoin state
  const [btcAddress, setBtcAddress] = useState(null);
  const [btcQrCodeUrl, setBtcQrCodeUrl] = useState(null);
  const [btcLoading, setBtcLoading] = useState(false);
  const [btcError, setBtcError] = useState('');
  const [copyMessage, setCopyMessage] = useState('');

  const numericDepixAmount = useMemo(() => parseCurrencyInput(depixAmountInput), [depixAmountInput]);

  // Os limites antifraude são resolvidos no backend a partir do nível de KYC do usuário.
  const fetchDepixLimits = useCallback(async () => {
    if (!user?.id) return;
    try {
      setDepixLimitsLoading(true);
      const response = await api.get('/deposits/pix/limits');
      setDepixLimits(response.data || null);
      setDepixLimitsError('');
    } catch (error) {
      setDepixLimits(null);
      setDepixLimitsError(
        error.response?.data?.message ||
          'Não foi possível carregar os limites de depósito. Tente novamente.'
      );
    } finally {
      setDepixLimitsLoading(false);
    }
  }, [user?.id]);

  const resetDepixFlow = () => {
    setDepixAmountInput('');
    setDepixError('');
    setDepixGlobalError('');
    setDepixRefusalCode('');
    setDepixResult(null);
    setDepixLoading(false);
    setCopyMessage('');
    setDepixPaymentStatus('idle');
    setDepixPollAttempt(0);
  };

  const handleContinueWaiting = () => {
    setDepixPollAttempt((attempt) => attempt + 1);
    setDepixPaymentStatus('polling');
  };

  // Polling a cada 5s para confirmar o pagamento do depósito Pix/DePix.
  // A fonte de verdade é o status da transação no backend (GET /trx/:uuid),
  // que é atualizado pelo webhook da BIA quando o pagamento é liquidado.
  useEffect(() => {
    const transactionUuid = depixResult?.transactionUuid;
    if (!transactionUuid || depixPaymentStatus !== 'polling') {
      return undefined;
    }

    const POLL_INTERVAL_MS = 5000;
    const POLL_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutos
    const startedAt = Date.now();
    let active = true;
    let intervalId;

    const checkStatus = async () => {
      if (Date.now() - startedAt >= POLL_TIMEOUT_MS) {
        clearInterval(intervalId);
        if (active) setDepixPaymentStatus('timeout');
        return;
      }
      try {
        const { data } = await api.get(`/deposits/pix/status/${transactionUuid}`);
        if (!active) return;
        if (data?.paid) {
          clearInterval(intervalId);
          posthog.capture('deposit_pix_confirmed', { transactionUuid });
          setDepixPaymentStatus('confirmed');
        } else if (data?.canceled) {
          clearInterval(intervalId);
          setDepixPaymentStatus('canceled');
        }
      } catch (error) {
        // Erros transitórios de rede são ignorados; segue tentando até o timeout.
      }
    };

    intervalId = setInterval(checkStatus, POLL_INTERVAL_MS);

    return () => {
      active = false;
      clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depixResult?.transactionUuid, depixPaymentStatus, depixPollAttempt]);

  // Após confirmar o pagamento, redireciona para o painel em ~3s.
  useEffect(() => {
    if (depixPaymentStatus !== 'confirmed') {
      return undefined;
    }
    const timeoutId = setTimeout(() => navigate('/dashboard'), 3000);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depixPaymentStatus]);

  useEffect(() => {
    if (method !== 'depix') return;
    fetchDepixLimits();
  }, [method, fetchDepixLimits]);

  const depixLimitsHint = useMemo(() => describeLimits(depixLimits), [depixLimits]);
  const depixBlockedReason = useMemo(() => getBlockedReason(depixLimits), [depixLimits]);
  const depixUpgradePath = useMemo(
    () => getUpgradePath(depixLimits, depixRefusalCode),
    [depixLimits, depixRefusalCode]
  );

  const depixDailyRemaining = useMemo(() => describeAvailableToday(depixLimits), [depixLimits]);

  const depixAmountValidation = useMemo(
    () => validateAmount(numericDepixAmount, depixLimits),
    [numericDepixAmount, depixLimits]
  );
  const isDepixAmountValid = depixAmountValidation === '';

  // Feature flags de rollout dos novos métodos de depósito (PostHog).
  useEffect(() => {
    const unsubscribe = posthog.onFeatureFlags(() => {
      setLnDepositEnabled(posthog.isFeatureEnabled('deposito-ln') === true);
      setLiquidDepositEnabled(posthog.isFeatureEnabled('deposito-liquid') === true);
    });
    return unsubscribe;
  }, []);

  // Se a flag do método selecionado estiver desligada, volta para o Depix.
  useEffect(() => {
    if (method === 'ln' && !lnDepositEnabled) setMethod('depix');
    if (method === 'liquid' && !liquidDepositEnabled) setMethod('depix');
  }, [method, lnDepositEnabled, liquidDepositEnabled]);

  useEffect(() => {
    if (method !== 'bitcoin' || !user?.id) {
      return;
    }

    const fetchAddress = async () => {
      try {
        setBtcLoading(true);
        setBtcError('');
        setCopyMessage('');
        const response = await api.get(`/deposits/bitcoin/${user.id}`);
        setBtcAddress(response.data?.address || null);
        setBtcQrCodeUrl(response.data?.qrCodeUrl || null);
      } catch (error) {
        setBtcError(
          error.response?.data?.message ||
            error.message ||
            'Não foi possível carregar o endereço Bitcoin.'
        );
      } finally {
        setBtcLoading(false);
      }
    };

    fetchAddress();
  }, [method, user?.id]);

  const handleMethodChange = (next) => {
    setMethod(next);
    resetDepixFlow();
    setBtcError('');
  };

  const handleDepixSubmit = async (event) => {
    event.preventDefault();
    if (!user?.id) {
      setDepixGlobalError('Sessão expirada. Faça login novamente.');
      return;
    }
    if (!isEmailVerified || !isLevelOneOrAbove) {
      setDepixGlobalError('Depósitos em reais são liberados apenas após a validação de email e nível 1.');
      return;
    }
    if (depixAmountValidation) {
      setDepixError(depixAmountValidation);
      return;
    }

    try {
      setDepixLoading(true);
      setDepixError('');
      setDepixGlobalError('');
      setDepixRefusalCode('');
      setDepixResult(null);

      const response = await api.post('/deposits/pix', {
        userId: user.id,
        userUuid: user?.uuid || user?.Uuid || user?.UUID || user?.userUuid || user?.UserUuid || null,
        amount: numericDepixAmount,
      });
      const result = response.data || {};
      setDepixResult(result);
      setDepixPaymentStatus(result?.transactionUuid ? 'polling' : 'idle');
      setDepixPollAttempt(0);
      posthog.capture('deposit_pix_initiated', { amount: numericDepixAmount });
      // O depósito recém-criado consome cooldown e janela, então os limites mudaram.
      fetchDepixLimits();
    } catch (error) {
      const refusalCode = error.response?.data?.code;
      setDepixGlobalError(
        refusalCode
          ? describeRefusal(error.response?.data?.message, error.response?.data?.retryAfter)
          : error.response?.data?.message ||
              error.message ||
              'Não foi possível gerar as instruções de depósito.'
      );
      if (refusalCode) {
        setDepixRefusalCode(refusalCode);
        posthog.capture('deposit_pix_refused', { code: refusalCode, amount: numericDepixAmount });
        // Recusa por regra de limite: recarrega para a tela refletir o novo estado.
        fetchDepixLimits();
      }
    } finally {
      setDepixLoading(false);
    }
  };

  const handleCopy = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyMessage('Copiado!');
      setTimeout(() => setCopyMessage(''), 2000);
      if (type === 'pix') posthog.capture('deposit_pix_code_copied');
      if (type === 'btc') posthog.capture('deposit_bitcoin_address_copied');
    } catch (error) {
      setCopyMessage('Não foi possível copiar.');
      setTimeout(() => setCopyMessage(''), 2000);
    }
  };

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  return (
    <div className="min-h-screen bg-[#f7f7f7] text-slate-700">
      <div className="min-h-screen flex flex-col lg:flex-row">
        <div className="lg:hidden">
          <div className="flex items-center justify-between px-6 py-4 bg-white shadow-sm">
            <button
              type="button"
              className="text-2xl text-slate-700"
              onClick={toggleSidebar}
              aria-label="Abrir menu"
            >
              <i className="bi-list"></i>
            </button>
            <img src="/img/branded/logob.png" alt="Stackfy" className="h-8" />
            <div className="w-7" />
          </div>
        </div>

        <InternalSidebar active="deposit" open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

        {sidebarOpen && (
          <button
            type="button"
            className="fixed inset-0 bg-slate-900/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
            aria-label="Fechar menu"
          ></button>
        )}

        <main className="flex-1 px-6 py-8 lg:px-10 lg:py-12">
          <EmailVerificationBanner />
          <header className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="text-sm font-semibold flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
            >
              <i className="bi-arrow-left-short text-xl"></i> Voltar
            </button>

            <InternalTopMenu onLogout={logout} />
          </header>

          <section className="mx-auto max-w-3xl">
            {!isEmailVerified || !isLevelOneOrAbove ? (
              <div className="mb-6 flex flex-col gap-4 rounded-3xl border border-amber-200 bg-amber-50 px-6 py-5 text-sm text-amber-900 shadow-sm md:flex-row md:items-center md:justify-between">
                <div className="space-y-1">
                  <h2 className="text-base font-semibold text-amber-900">
                    Complete o nível 1 para depositar em reais
                  </h2>
                  <p>
                    Depósitos em reais exigem email verificado.
                    <br />
                    Conclua sua verificação para ativar esta funcionalidade.
                  </p>
                </div>
                <a
                  href="/account/level"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-amber-500 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-amber-600"
                >
                  Níveis de conta
                  <i className="bi-arrow-right-short text-lg"></i>
                </a>
              </div>
            ) : null}
            {method === 'depix' && depixResult ? (
              <div className="rounded-3xl border border-slate-200 bg-white px-6 py-6 shadow-sm sm:px-10 sm:py-10 space-y-8">
                {depixPaymentStatus === 'confirmed' ? (
                  <div className="flex flex-col items-center gap-4 py-10 text-center">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                      <i className="bi-check-circle text-3xl"></i>
                    </div>
                    <h1 className="text-2xl font-semibold text-slate-900">Pagamento confirmado!</h1>
                    <p className="text-sm text-slate-500 max-w-md">
                      Recebemos a confirmação do seu depósito. Redirecionando para o painel...
                    </p>
                    <button
                      type="button"
                      onClick={() => navigate('/dashboard')}
                      className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-[#0255F1] px-6 py-3 text-sm font-semibold text-white hover:bg-[#0144CC] transition-colors"
                    >
                      Ir para o painel agora
                    </button>
                  </div>
                ) : (
                <>
                <div>
                  <h1 className="text-xl font-semibold text-slate-900">Pagamento via Pix</h1>
                  <p className="text-sm text-slate-500 max-w-2xl">
                    Utilize o QR Code ou o código copia e cola para concluir o pagamento.
                    <br /> Após transferir o valor, selecione <strong>“Já fiz o pagamento”</strong> para nos avisar.
                  </p>
                </div>

                <div className="flex flex-col gap-8 lg:flex-row lg:items-start">
                  <div className="flex flex-col items-center gap-4 lg:w-5/12 lg:items-start">
                    <div className="w-full">
                      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">QR Code</span>
                    </div>
                    <div className="flex h-72 w-full max-w-sm items-center justify-center rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                      {depixResult.qrCode?.imageUrl ? (
                        <img
                          src={depixResult.qrCode.imageUrl}
                          alt="QR Code Pix"
                          className="h-full w-full object-contain"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-slate-50 text-xs text-slate-400">
                          QR Code indisponível
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex-1 space-y-6">
                    {depixResult.qrCode?.payload ? (
                      <div className="w-full">
                        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                          Código copia e cola
                        </label>
                        <div className="mt-2 flex flex-col gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3">
                          <textarea
                            readOnly
                            className="w-full resize-none bg-transparent text-xs text-slate-600 outline-none"
                            rows={3}
                            value={depixResult.qrCode.payload}
                          />
                          <div className="flex flex-wrap items-center gap-3">
                            <button
                              type="button"
                              onClick={() => handleCopy(depixResult.qrCode.payload, 'pix')}
                              className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-slate-700"
                            >
                              <i className="bi-clipboard"></i> Copiar código
                            </button>
                            {copyMessage ? (
                              <span className="text-xs font-semibold text-slate-500">{copyMessage}</span>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    ) : null}

                    <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-600">
                      <dl className="space-y-2">
                        <div className="flex items-center justify-between">
                          <dt>Valor do depósito</dt>
                          <dd className="font-semibold text-slate-900">
                            {(() => {
                              const value = depixResult.amount?.formatted || formatCurrency(numericDepixAmount);
                              return value.startsWith('R$') ? value : `R$ ${value}`;
                            })()}
                          </dd>
                        </div>
                        <div className="flex items-center justify-between">
                          <dt>Taxa de processamento</dt>
                          <dd className="font-semibold text-slate-900">
                            {(() => {
                              const value = depixResult.fee?.formatted || '0,99';
                              return value.startsWith('R$') ? value : `R$ ${value}`;
                            })()}
                          </dd>
                        </div>
                        <div className="flex items-center justify-between">
                          <dt>Total que será creditado</dt>
                          <dd className="text-base font-semibold text-emerald-600">
                            {(() => {
                              const value = depixResult.total?.formatted || formatCurrency((numericDepixAmount || 0) - 0.99);
                              return value.startsWith('R$') ? value : `R$ ${value}`;
                            })()}
                          </dd>
                        </div>
                      </dl>
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                      {depixResult.confirmationUrl ? (
                        <button
                          type="button"
                          onClick={() => {
                            navigate('/dashboard');
                          }}
                          className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#0255F1] px-5 py-3 text-sm font-semibold text-white hover:bg-[#0144CC] transition-colors"
                        >
                          <i className="bi-check-circle"></i> Já fiz o pagamento
                        </button>
                      ) : (
                        <span className="text-xs font-medium text-slate-500">Aguardando dados do pagamento</span>
                      )}
                    </div>

                    {depixPaymentStatus === 'polling' ? (
                      <div className="flex items-center justify-center gap-2 rounded-xl bg-slate-50 px-4 py-3 text-xs font-medium text-slate-500">
                        <span className="h-3 w-3 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600"></span>
                        Aguardando confirmação do pagamento...
                      </div>
                    ) : null}

                    {depixPaymentStatus === 'timeout' ? (
                      <div className="flex flex-col items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-4 text-center text-xs text-amber-700">
                        <span>
                          Ainda não identificamos o pagamento. Se você já pagou, a confirmação pode levar mais alguns minutos.
                        </span>
                        <button
                          type="button"
                          onClick={handleContinueWaiting}
                          className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-slate-700"
                        >
                          Continuar aguardando
                        </button>
                      </div>
                    ) : null}

                    {depixPaymentStatus === 'canceled' ? (
                      <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-center text-xs font-medium text-red-600">
                        Este pagamento foi cancelado. Gere um novo depósito para tentar novamente.
                      </div>
                    ) : null}
                  </div>
                </div>
                </>
                )}
              </div>
            ) : (
              <div className="rounded-3xl border border-slate-200 bg-white px-6 py-6 shadow-sm sm:px-10 sm:py-10">
                <div className="mb-6">
                  <h1 className="text-xl font-semibold text-slate-900">Depositar</h1>
                  <p className="text-sm text-slate-500">Escolha uma opção de depósito para continuar.</p>
                </div>

                <div className="mb-8 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => handleMethodChange('depix')}
                    className={`flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold transition-colors ${
                      method === 'depix'
                        ? 'bg-slate-900 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    <i className="bi-cash-coin"></i> R$ Depix
                  </button>
                  <button
                    type="button"
                    onClick={() => handleMethodChange('bitcoin')}
                    className={`flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold transition-colors ${
                      method === 'bitcoin'
                        ? 'bg-slate-900 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    <i className="bi-currency-bitcoin"></i> Bitcoin
                  </button>
                  {lnDepositEnabled ? (
                    <button
                      type="button"
                      onClick={() => handleMethodChange('ln')}
                      className={`flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold transition-colors ${
                        method === 'ln'
                          ? 'bg-slate-900 text-white'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      <i className="bi-lightning-charge-fill"></i> Lightning
                    </button>
                  ) : null}
                  {liquidDepositEnabled ? (
                    <button
                      type="button"
                      onClick={() => handleMethodChange('liquid')}
                      className={`flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold transition-colors ${
                        method === 'liquid'
                          ? 'bg-slate-900 text-white'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      <i className="bi-droplet-fill"></i> Liquid
                    </button>
                  ) : null}
                </div>

                {depixGlobalError ? (
                  <div className="mb-4 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                    {depixGlobalError}
                  </div>
                ) : null}

                {method === 'depix' && depixLimitsError && !depixResult ? (
                  <div className="mb-4 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                    {depixLimitsError}
                  </div>
                ) : null}

                {method === 'depix' && depixBlockedReason && !depixResult ? (
                  <div className="mb-4 rounded border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
                    {depixBlockedReason}
                  </div>
                ) : null}

                {method === 'depix' && depixUpgradePath ? (
                  <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                    <span>{depixUpgradePath}</span>
                    <button
                      type="button"
                      onClick={() => navigate('/account/level')}
                      className="rounded-full bg-slate-900 px-4 py-1.5 text-xs font-semibold text-white"
                    >
                      Verificar conta
                    </button>
                  </div>
                ) : null}

                {method === 'depix' ? (
                  <form className="space-y-6" onSubmit={handleDepixSubmit}>
                    <div>
                      <label className="block text-sm font-medium text-slate-600 mb-2">
                        Valor do depósito
                      </label>
                      <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-[#f3f3f3] px-4 py-3 text-sm font-medium text-slate-700 focus-within:border-slate-400">
                        <span className="text-slate-400">R$</span>
                        <input
                          type="text"
                          inputMode="decimal"
                          placeholder="0,00"
                          className="w-full bg-transparent outline-none placeholder:text-slate-400"
                          value={depixAmountInput}
                          onChange={(event) => {
                            setDepixError('');
                            const rawDigits = event.target.value.replace(/\D/g, '');
                            const numeric = Number(rawDigits) / 100;
                            if (Number.isNaN(numeric)) {
                              setDepixAmountInput('');
                              return;
                            }
                            setDepixAmountInput(formatCurrency(numeric));
                          }}
                          onFocus={(event) => event.target.select()}
                        />
                      </div>
                      {depixError ? (
                        <p className="mt-2 text-xs text-red-600">{depixError}</p>
                      ) : null}
                      <p className="mt-2 text-xs text-slate-500">
                        {depixLimitsLoading && !depixLimits
                          ? 'Carregando limites de depósito...'
                          : depixLimitsHint}
                      </p>
                      {!depixLimits && !depixLimitsLoading ? (
                        <button
                          type="button"
                          onClick={fetchDepixLimits}
                          className="mt-2 text-xs font-semibold text-tBlue underline"
                        >
                          Recarregar limites de depósito
                        </button>
                      ) : null}
                      {depixDailyRemaining ? (
                        <p className="mt-1 text-xs text-slate-500">{depixDailyRemaining}</p>
                      ) : null}
                    </div>

                    <button
                      type="submit"
                      className="w-full rounded-full bg-tBlue py-3 text-sm font-semibold text-white transition-colors hover:bg-tBlue2 disabled:opacity-60"
                      disabled={
                        !isDepixAmountValid ||
                        depixLoading ||
                        depixLimitsLoading ||
                        // Sem os limites carregados não dá para saber o que é permitido, então o
                        // botão não é liberado às cegas.
                        !depixLimits ||
                        Boolean(depixLimits?.blocked) ||
                        !isEmailVerified ||
                        !isLevelOneOrAbove
                      }
                    >
                      {depixLoading ? 'Gerando instruções...' : 'Gerar instruções Pix'}
                    </button>
                  </form>
                ) : method === 'ln' || method === 'liquid' ? (
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-6 py-10 text-center">
                    <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-slate-900 text-white">
                      <i
                        className={`${
                          method === 'ln' ? 'bi-lightning-charge-fill' : 'bi-droplet-fill'
                        } text-xl`}
                      ></i>
                    </div>
                    <h2 className="text-base font-semibold text-slate-800">
                      Depósito via {method === 'ln' ? 'Lightning Network' : 'Liquid Network'}
                    </h2>
                    <p className="mx-auto mt-2 max-w-md text-sm text-slate-500">
                      Em breve você poderá depositar via{' '}
                      {method === 'ln' ? 'Lightning' : 'Liquid'}. Estamos finalizando essa
                      integração.
                    </p>
                    <span className="mt-4 inline-flex items-center gap-2 rounded-full bg-slate-200 px-4 py-1.5 text-xs font-semibold text-slate-500">
                      Em breve
                    </span>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {btcError ? (
                      <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                        {btcError}
                      </div>
                    ) : (
                      <div className="rounded-2xl border border-slate-200 bg-slate-50 px-6 py-6">
                        <h2 className="text-sm font-semibold text-slate-800">Enviar Bitcoin</h2>
                        <p className="text-sm text-slate-500">
                          Envie BTC para o endereço abaixo. A confirmação pode levar alguns minutos na rede.
                        </p>

                        {btcLoading ? (
                          <div className="mt-6 inline-flex items-center gap-2 text-sm text-slate-500">
                            <span className="inline-flex h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-slate-500"></span>
                            Carregando endereço...
                          </div>
                        ) : (
                          <>
                            <div className="mt-6 flex flex-col gap-8 lg:flex-row lg:items-start">
                              <div className="flex flex-col gap-4 lg:w-5/12">
                                <div>
                                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">QR Code</span>
                                </div>
                                <div className="flex h-72 w-full max-w-sm items-center justify-center rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                                  {btcQrCodeUrl ? (
                                    <img
                                      src={btcQrCodeUrl}
                                      alt="QR Code Bitcoin"
                                      className="h-full w-full object-contain"
                                    />
                                  ) : (
                                    <div className="flex h-full w-full items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-slate-50 text-xs text-slate-400">
                                      QR Code indisponível
                                    </div>
                                  )}
                                </div>
                              </div>

                              <div className="flex-1 space-y-6">
                                <div className="space-y-2">
                                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide">
                                    Endereço Bitcoin
                                  </label>
                                  <div className="flex flex-col gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3">
                                    <code className="break-all text-sm text-slate-700">{btcAddress || '—'}</code>
                                    {btcAddress ? (
                                      <div className="flex items-center gap-3">
                                        <button
                                          type="button"
                                          onClick={() => handleCopy(btcAddress, 'btc')}
                                          className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-slate-700"
                                        >
                                          <i className="bi-clipboard"></i> Copiar endereço
                                        </button>
                                        {copyMessage ? (
                                          <span className="text-xs font-semibold text-slate-500">{copyMessage}</span>
                                        ) : null}
                                      </div>
                                    ) : null}
                                  </div>
                                </div>
                                <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
                                  <p className="text-xs text-slate-500">
                                    Depósitos são creditados após a confirmação na blockchain.
                                  </p>
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </section>
        </main>
      </div>
    </div>
  );
};

export default Deposit;


